import React from 'react';
import { Zap } from 'lucide-react';

interface ElectricEffectProps {
  isElectric?: boolean;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  intensity?: 'low' | 'medium' | 'high';
}

const ElectricEffect: React.FC<ElectricEffectProps> = ({
  isElectric = false,
  className = '',
  size = 'medium',
  intensity = 'medium'
}) => {
  if (!isElectric) return null;

  const sizeMap = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-6 h-6'
  };

  const particleCount = {
    low: 3,
    medium: 5,
    high: 7
  };

  const zapSize = sizeMap[size];
  const count = particleCount[intensity];

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      {/* Lightning bolts */}
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="absolute animate-pulse"
          style={{
            left: `${12 + (i * 22) % 76}%`,
            top: `${12 + (i * 18) % 76}%`,
            animationDelay: `${i * 250}ms`,
            animationDuration: `${1200 + i * 200}ms`
          }}
        >
          <Zap
            className={`text-yellow-400 ${zapSize}`}
            fill="currentColor"
            style={{
              filter: 'drop-shadow(0 0 4px rgba(250, 204, 21, 0.9))',
              transform: `rotate(${-30 + i * 20}deg)`
            }}
          />
        </div>
      ))}

      {/* Electric sparks */}
      {Array.from({ length: Math.floor(count * 1.2) }).map((_, i) => (
        <div
          key={`spark-${i}`}
          className="absolute animate-ping"
          style={{
            left: `${15 + (i * 20) % 70}%`,
            top: `${15 + (i * 25) % 70}%`,
            animationDelay: `${i * 150 + 300}ms`,
            animationDuration: `${1000 + i * 300}ms`
          }}
        >
          <div
            className="w-1 h-1 bg-cyan-300 rounded-full"
            style={{
              filter: 'drop-shadow(0 0 3px rgba(103, 232, 249, 1))',
              boxShadow: '0 0 5px rgba(103, 232, 249, 0.8), 0 0 8px rgba(250, 204, 21, 0.6)'
            }}
          />
        </div>
      ))}

      {/* Electric aura */}
      <div
        className="absolute inset-0 rounded-full opacity-30"
        style={{
          background: 'radial-gradient(circle, rgba(250, 204, 21, 0.3) 0%, rgba(103, 232, 249, 0.2) 40%, transparent 70%)',
          animation: 'pulse 1.5s ease-in-out infinite'
        }}
      />

      {/* Crackling energy */}
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={`crackle-${i}`}
          className="absolute"
          style={{
            left: `${-4 + (i * 108) % 108}%`,
            top: `${-4 + (i * 72) % 108}%`,
            animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
            animationDelay: `${i * 500}ms`
          }}
        >
          <div
            className="w-0.5 h-3 bg-gradient-to-b from-yellow-300 to-transparent rounded-full"
            style={{
              filter: 'drop-shadow(0 0 2px rgba(250, 204, 21, 0.9))',
              transform: `rotate(${i * 45}deg)`,
              boxShadow: '0 0 4px rgba(250, 204, 21, 0.7)'
            }}
          />
        </div>
      ))}

      {/* Floating electric orbs */}
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={`orb-${i}`}
          className="absolute animate-bounce"
          style={{
            left: `${5 + (i * 90) % 90}%`,
            top: `${5 + (i * 85) % 90}%`,
            animationDelay: `${i * 600 + 200}ms`,
            animationDuration: `${2200 + i * 400}ms`
          }}
        >
          <Zap
            className="text-cyan-400 w-2 h-2"
            fill="currentColor"
            style={{
              filter: 'drop-shadow(0 0 2px rgba(103, 232, 249, 1))',
              transform: `rotate(${i * 90}deg)`
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default ElectricEffect;
